import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { LoadingSpinner, EmptyState } from '../components';

const STATUS_COLOR = {
  pending: '#F59E0B', new: '#F59E0B', accepted: '#3B82F6',
  preparing: '#8B5CF6', ready: '#10B981', completed: '#10B981',
  delivered: '#10B981', cancelled: '#EF4444', rejected: '#EF4444',
};
const STATUS_LABEL = {
  pending: 'Order Placed', new: 'Order Placed', accepted: 'Accepted',
  preparing: 'Preparing', ready: 'Ready for Pickup', completed: 'Completed',
  delivered: 'Delivered', cancelled: 'Cancelled', rejected: 'Rejected',
};
const STATUS_ORDER = ['pending', 'new', 'accepted', 'preparing', 'ready', 'completed', 'delivered'];

function fmt(amount) {
  const n = parseFloat(amount);
  return isNaN(n) ? '$0.00' : `$${n.toFixed(2)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return 'N/A'; }
}

const OrderStatusBar = ({ status }) => {
  const activeStatuses = ['new', 'pending', 'accepted', 'preparing', 'ready', 'completed'];
  const steps = [
    { key: 'pending', label: 'Placed' },
    { key: 'accepted', label: 'Accepted' },
    { key: 'preparing', label: 'Preparing' },
    { key: 'ready', label: 'Ready' },
    { key: 'completed', label: 'Done' },
  ];
  const isCancelled = ['cancelled', 'rejected'].includes(status);
  const currentIdx = steps.findIndex(s => s.key === status || (status === 'new' && s.key === 'pending'));

  if (isCancelled) {
    return (
      <div className="order-status-bar cancelled">
        <span className="cancelled-badge">❌ Order {STATUS_LABEL[status] || status}</span>
      </div>
    );
  }

  return (
    <div className="order-status-bar">
      {steps.map((step, idx) => (
        <React.Fragment key={step.key}>
          <div className={`status-step ${idx <= currentIdx ? 'done' : ''} ${idx === currentIdx ? 'active' : ''}`}>
            <div className="status-dot">{idx < currentIdx ? '✓' : idx + 1}</div>
            <span className="status-step-label">{step.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`status-connector ${idx < currentIdx ? 'done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const OrderCard = ({ order, onClick }) => {
  const status = order.status || 'pending';
  const color = STATUS_COLOR[status] || '#888';
  const label = STATUS_LABEL[status] || status;
  const orderId = order.order_id || order.id;
  const itemCount = order.items?.length || 0;
  const total = fmt(order.total_amount || order.total || 0);

  return (
    <div className="order-card" onClick={() => onClick(orderId)}>
      <div className="order-card-header">
        <div>
          <span className="order-number">Order #{order.order_number || orderId}</span>
          <span className="order-date">{formatDate(order.created_at)}</span>
        </div>
        <span className="order-status-badge" style={{ background: color + '20', color }}>
          {label}
        </span>
      </div>
      <div className="order-kitchen-name">🍽️ {order.kitchen_name || 'Kitchen'}</div>
      <div className="order-card-footer">
        <span className="order-items-count">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
        <span className="order-total">{total}</span>
      </div>
    </div>
  );
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'history'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    loadOrders();
  }, [isAuthenticated, activeTab]);

  const loadOrders = async (loadPage = 1) => {
    setIsLoading(true);
    try {
      const filters = { limit: 20, offset: (loadPage - 1) * 20 };
      if (activeTab === 'active') {
        filters.status = 'new,pending,accepted,preparing,ready';
      }
      const res = await apiService.getOrderHistory(filters);
      const list = res?.orders || res?.data?.orders || (Array.isArray(res?.data) ? res.data : []);
      if (loadPage === 1) {
        setOrders(list);
      } else {
        setOrders(prev => [...prev, ...list]);
      }
      setHasMore(list.length === 20);
      setPage(loadPage);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const activeOrders = orders.filter(o => ['new','pending','accepted','preparing','ready'].includes(o.status));
  const pastOrders = orders.filter(o => !['new','pending','accepted','preparing','ready'].includes(o.status));
  const displayOrders = activeTab === 'active' ? activeOrders : pastOrders;

  return (
    <div className="orders-page">
      <div className="orders-header">
        <h2 className="page-title">My Orders</h2>
      </div>

      <div className="orders-tabs">
        <button
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {isLoading && orders.length === 0 ? (
        <LoadingSpinner text="Loading orders..." />
      ) : displayOrders.length === 0 ? (
        <EmptyState
          icon="📦"
          title={activeTab === 'active' ? 'No active orders' : 'No past orders'}
          subtitle={activeTab === 'active' ? 'Your current orders will appear here' : 'Your order history will appear here'}
          action={{ label: 'Browse Kitchens', onClick: () => navigate('/customer') }}
        />
      ) : (
        <div className="orders-list">
          {displayOrders.map(order => (
            <OrderCard
              key={order.order_id || order.id}
              order={order}
              onClick={(id) => navigate(`/customer/orders/${id}`)}
            />
          ))}
          {hasMore && !isLoading && (
            <button className="load-more-btn" onClick={() => loadOrders(page + 1)}>
              Load More
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
